import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  LinearProgress,
  Container,
  Grid,
  Avatar,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepIcon,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  FastForward as FastForwardIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  CheckCircle as CheckCircleIcon,
  Rocket as RocketIcon,
  Menu as MenuIcon,
  Book as BookIcon,
  Map as MapIcon,
  Task as TaskIcon,
  Architecture as ArchitectureIcon,
  Code as CodeIcon,
  Article as ArticleIcon,
  DataObject as DataIcon,
} from '@mui/icons-material';
import {
  IncubationSession,
  ChatMessage,
  WorkflowStepType,
  WorkflowState,
  SessionParametersModel,
  Epic,
  UserStory,
} from '../models/incubation';
import { IncubationService } from '../services/incubationService';
import MessageComponent from './MessageComponent';
import EditableNarrative from './EditableNarrative';

const drawerWidth = 280;

export const IncubationStudioSession = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sessions, setSessions] = useState<IncubationSession[]>([]);
  const [currentSession, setCurrentSession] = useState<IncubationSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isNewSession, setIsNewSession] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [editingNarrative, setEditingNarrative] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Form state for new session
  const [sessionParams, setSessionParams] = useState<SessionParametersModel>({
    originalAsk: '',
    projectName: '',
    targetPlatform: 'web',
    preferredStack: '',
  });

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [currentSession?.messages]);

  const loadSessions = () => {
    const savedSessions = IncubationService.getFromLocalStorage();
    setSessions(savedSessions);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const startNewSession = async () => {
    if (!sessionParams.originalAsk.trim()) return;

    setLoading(true);
    setProcessingMessage('Initializing your incubation workflow...');

    try {
      // Call the new API endpoint to start a workflow session
      const workflowResponse = await IncubationService.startWorkflowSession(
        sessionParams.originalAsk,
        true, // generateInitialNarrative
        256   // maxTokens
      );

      // Extract the generated narrative from the nested structure
      let generatedNarrative = workflowResponse.session?.systemScope?.systemNarrative?.generatedNarrative;
      
      // Parse JSON if the narrative contains JSON structure
      if (generatedNarrative && generatedNarrative.includes('```json')) {
        try {
          const jsonMatch = generatedNarrative.match(/```json\n([\s\S]*?)\n```/);
          if (jsonMatch && jsonMatch[1]) {
            const parsed = JSON.parse(jsonMatch[1]);
            generatedNarrative = parsed.GeneratedNarrative || generatedNarrative;
          }
        } catch (e) {
          console.warn('Failed to parse JSON from narrative:', e);
        }
      }
      
      const newSession: IncubationSession = {
        id: workflowResponse.sessionId || `session_${Date.now()}`,
        originalAsk: sessionParams.originalAsk,
        state: WorkflowState.InProgress,
        lastExecutedStep: WorkflowStepType.None, // Set to None initially since narrative needs to be confirmed
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
        messages: [],
        generatedCode: [],
      };

      // Add initial user message
      const userMessage: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content: `<strong>Project Idea:</strong><br/>${sessionParams.originalAsk}`,
        timestamp: new Date(),
      };
      newSession.messages.push(userMessage);

      // Add the initial narrative from the API response as an assistant message
      if (generatedNarrative) {
        const narrativeMessage: ChatMessage = {
          id: `msg_${Date.now()}_narrative`,
          role: 'assistant',
          content: `
            <h3>Generated System Narrative</h3>
            <p>${generatedNarrative}</p>
            <br/>
            <em>You can edit this narrative before proceeding to the next step.</em>
          `,
          timestamp: new Date(),
          stepType: WorkflowStepType.SystemNarrative,
          metadata: {
            editable: true,
            isGeneratedNarrative: true,
            rawNarrative: generatedNarrative
          }
        };
        newSession.messages.push(narrativeMessage);

        // Store the narrative in the session
        newSession.systemNarrative = generatedNarrative;
      }

      setCurrentSession(newSession);
      setIsNewSession(false);

      // Save to local storage
      IncubationService.saveToLocalStorage(newSession);
      loadSessions();

    } catch (error) {
      console.error('Error starting session:', error);
      setProcessingMessage('Error starting session: ' + (error as Error).message);
    } finally {
      setLoading(false);
      setProcessingMessage('');
    }
  };

  const executeNextStep = async (session: IncubationSession = currentSession!) => {
    if (!session || loading) return;

    setLoading(true);
    
    try {
      const nextStep = getNextStep(session.lastExecutedStep);
      if (nextStep === WorkflowStepType.None) {
        session.state = WorkflowState.Completed;
        setCurrentSession(session);
        return;
      }

      // Add user action message if there are additional instructions
      if (additionalInstructions.trim()) {
        const userAction: ChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'user',
          content: `<strong>Additional Instructions for ${getStepDisplayName(nextStep)}:</strong><br/>${additionalInstructions}`,
          timestamp: new Date(),
        };
        session.messages.push(userAction);
      }

      setProcessingMessage(`Executing ${getStepDisplayName(nextStep)}...`);

      // Execute the workflow step with the correct API parameters
      const result = await IncubationService.executeWorkflowStep(
        session.id,
        nextStep,
        1024 // maxTokens
      );

      // Update session with results
      session.lastExecutedStep = nextStep;
      updateSessionWithResults(session, nextStep, result);

      // Add result message
      const message: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: formatStepContent(nextStep, result),
        timestamp: new Date(),
        stepType: nextStep,
      };

      session.messages.push(message);
      session.lastUpdatedAt = new Date();

      // Clear additional instructions
      setAdditionalInstructions('');

      // Save progress
      IncubationService.saveToLocalStorage(session);
      setCurrentSession({ ...session });
    } catch (error) {
      console.error('Error executing step:', error);
      setProcessingMessage('Error executing step');
    } finally {
      setLoading(false);
      setProcessingMessage('');
    }
  };

  const getNextStep = (currentStep: WorkflowStepType): WorkflowStepType => {
    const steps = [
      WorkflowStepType.SystemNarrative,
      WorkflowStepType.TechnicalLandscape,
      WorkflowStepType.SystemRequirements,
      WorkflowStepType.ProjectPhases,
      WorkflowStepType.DataSchema,
      WorkflowStepType.ProjectBuild,
      WorkflowStepType.FinalDocumentation,
    ];

    const currentIndex = steps.indexOf(currentStep);
    return currentIndex < steps.length - 1 ? steps[currentIndex + 1] : WorkflowStepType.None;
  };

  const getStepDisplayName = (step: WorkflowStepType): string => {
    const names: Record<WorkflowStepType, string> = {
      [WorkflowStepType.None]: 'None',
      [WorkflowStepType.SystemNarrative]: 'System Narrative',
      [WorkflowStepType.TechnicalLandscape]: 'Technical Landscape',
      [WorkflowStepType.SystemRequirements]: 'Requirements Analysis',
      [WorkflowStepType.ProjectPhases]: 'Project Planning',
      [WorkflowStepType.DataSchema]: 'Data Architecture',
      [WorkflowStepType.ProjectBuild]: 'Code Generation',
      [WorkflowStepType.FinalDocumentation]: 'Documentation',
    };
    return names[step] || 'Unknown';
  };

  const getStepIcon = (step: WorkflowStepType): React.ReactNode => {
    const icons: Record<WorkflowStepType, React.ReactNode> = {
      [WorkflowStepType.None]: null,
      [WorkflowStepType.SystemNarrative]: <BookIcon />,
      [WorkflowStepType.TechnicalLandscape]: <MapIcon />,
      [WorkflowStepType.SystemRequirements]: <TaskIcon />,
      [WorkflowStepType.ProjectPhases]: <ArchitectureIcon />,
      [WorkflowStepType.DataSchema]: <DataIcon />,
      [WorkflowStepType.ProjectBuild]: <CodeIcon />,
      [WorkflowStepType.FinalDocumentation]: <ArticleIcon />,
    };
    return icons[step];
  };

  const updateSessionWithResults = (session: IncubationSession, step: WorkflowStepType, result: any) => {
    // The API response contains generatedContent and the updated session
    const generatedContent = result.generatedContent;
    const updatedSession = result.session;
    
    switch (step) {
      case WorkflowStepType.SystemNarrative:
        session.systemNarrative = updatedSession?.systemScope?.systemNarrative || generatedContent;
        break;
      case WorkflowStepType.TechnicalLandscape:
        session.technicalLandscape = updatedSession?.systemScope?.technicalLandscape || generatedContent;
        break;
      case WorkflowStepType.SystemRequirements:
        session.systemRequirements = updatedSession?.systemScope?.requirementsScope || generatedContent;
        break;
      case WorkflowStepType.ProjectPhases:
        session.projectPhases = updatedSession?.architectureScope?.projectDefinition || generatedContent;
        break;
      case WorkflowStepType.DataSchema:
        session.dataSchema = updatedSession?.architectureScope?.schema || generatedContent;
        break;
      case WorkflowStepType.ProjectBuild:
        session.projectBuild = updatedSession?.developmentScope?.backend || generatedContent;
        session.generatedCode = result.generatedCode || [];
        break;
      case WorkflowStepType.FinalDocumentation:
        session.finalDocumentation = updatedSession?.developmentScope?.documentation || generatedContent;
        break;
    }
  };

  const formatStepContent = (step: WorkflowStepType, result: any): string => {
    // Extract the generated content from the API response
    const generatedContent = result.generatedContent;
    const message = result.message || '';
    
    if (!generatedContent) {
      return message || 'Step completed successfully.';
    }
    
    // Format the content based on step type
    if (step === WorkflowStepType.SystemNarrative) {
      return formatSystemNarrative(generatedContent);
    } else if (step === WorkflowStepType.TechnicalLandscape) {
      return formatTechnicalLandscape(generatedContent);
    } else if (step === WorkflowStepType.SystemRequirements) {
      return formatSystemRequirements(generatedContent);
    } else {
      // For other steps, display the generated content as HTML
      return `
        <h3>${getStepDisplayName(step)}</h3>
        <div>${generatedContent}</div>
      `;
    }
  };

  const formatTechnicalLandscape = (landscape: any): string => {
    if (typeof landscape === 'string') {
      return landscape;
    }
    
    return `
      <h3>Technical Landscape</h3>
      ${landscape.platform ? `<p><strong>Platform:</strong> ${landscape.platform}</p>` : ''}
      ${landscape.techStack ? `<p><strong>Tech Stack:</strong> ${landscape.techStack.join(', ')}</p>` : ''}
      ${landscape.systemFeatures ? `<p><strong>Features:</strong> ${landscape.systemFeatures.join(', ')}</p>` : ''}
      ${landscape.architectureComponents ? `<p><strong>Architecture:</strong> ${JSON.stringify(landscape.architectureComponents, null, 2)}</p>` : ''}
    `;
  };

  const formatSystemRequirements = (requirements: any): string => {
    if (typeof requirements === 'string') {
      return requirements;
    }
    
    return `
      <h3>System Requirements</h3>
      ${requirements.epics ? `<p><strong>Epics:</strong> ${requirements.epics.length}</p>` : ''}
      ${requirements.userStories ? `<p><strong>User Stories:</strong> ${requirements.userStories.length}</p>` : ''}
    `;
  };

  const formatSystemNarrative = (narrative: any): string => {
    // If it's a SystemNarrative object, format it properly
    if (narrative.systemVision || narrative.systemDescription) {
      return `
        <h3>System Vision</h3>
        <p>${narrative.systemVision || ''}</p>
        <h3>System Description</h3>
        <p>${narrative.systemDescription || ''}</p>
        ${narrative.corePurpose ? `<h3>Core Purpose</h3><p>${narrative.corePurpose}</p>` : ''}
        ${narrative.targetUser ? `<h3>Target User</h3><p>${narrative.targetUser}</p>` : ''}
        ${narrative.keyFeatures ? `<h3>Key Features</h3><ul>${narrative.keyFeatures.map((f: string) => `<li>${f}</li>`).join('')}</ul>` : ''}
      `;
    }
    // If it's a simple object or string, return as-is
    return typeof narrative === 'string' ? narrative : JSON.stringify(narrative, null, 2);
  };

  const exportSession = async (format: string) => {
    if (!currentSession) return;

    try {
      const blob = await IncubationService.exportSession(currentSession.id, format);
      const filename = `incubation_${currentSession.id}_${Date.now()}.${format}`;
      IncubationService.downloadFile(blob, filename);
    } catch (error) {
      console.error('Error exporting session:', error);
    }
  };

  const sidebar = (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <img src="/images/smart-ai-logo-colored.svg" alt="SmartAI Logo" style={{ height: 40 }} />
        <Typography variant="h6" sx={{ mt: 1 }}>
          Incubation Studio
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1">Sessions</Typography>
          <IconButton size="small" onClick={() => setIsNewSession(true)}>
            <AddIcon />
          </IconButton>
        </Box>

        <List>
          {sessions.map((session) => (
            <ListItem
              key={session.id}
              disablePadding
              selected={currentSession?.id === session.id}
            >
              <ListItemButton onClick={() => setCurrentSession(session)}>
                <ListItemText
                  primary={session.originalAsk.substring(0, 30) + '...'}
                  secondary={new Date(session.createdAt).toLocaleDateString()}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  const stepperSteps = [
    WorkflowStepType.SystemNarrative,
    WorkflowStepType.TechnicalLandscape,
    WorkflowStepType.SystemRequirements,
    WorkflowStepType.ProjectPhases,
    WorkflowStepType.DataSchema,
    WorkflowStepType.ProjectBuild,
    WorkflowStepType.FinalDocumentation,
  ];

  const getStepperActiveStep = () => {
    if (!currentSession) return 0;
    return stepperSteps.indexOf(currentSession.lastExecutedStep) + 1;
  };

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {currentSession ? currentSession.originalAsk.substring(0, 50) + '...' : 'Incubation Studio'}
          </Typography>
          {currentSession && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="inherit" onClick={() => exportSession('json')}>
                <DownloadIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {sidebar}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
        }}
      >
        {isNewSession ? (
          // New session form
          <Container 
            maxWidth="lg"
            sx={{ 
              px: { xs: 1, sm: 2, md: 3 },
            }}
          >
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                  }}
                >
                  Start New Incubation
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                >
                  Describe your software idea, product vision, or business challenge.
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 4 : 6}
                  variant="outlined"
                  placeholder="What would you like to build?"
                  value={sessionParams.originalAsk}
                  onChange={(e) => setSessionParams({ ...sessionParams, originalAsk: e.target.value })}
                  sx={{ mb: { xs: 2, sm: 3 } }}
                />
                
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    }}
                  >
                    Your Incubation Journey
                  </Typography>
                  <Stepper 
                    activeStep={-1} 
                    alternativeLabel={!isMobile}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                  >
                    {stepperSteps.map((step) => (
                      <Step key={step}>
                        <StepLabel icon={getStepIcon(step)}>
                          <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {getStepDisplayName(step)}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </CardContent>
              <CardActions 
                sx={{ 
                  p: { xs: 2, sm: 3 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<RocketIcon />}
                  onClick={startNewSession}
                  disabled={!sessionParams.originalAsk.trim() || loading}
                  fullWidth={isMobile}
                >
                  Start Incubation
                </Button>
                <Button
                  size={isMobile ? 'medium' : 'large'}
                  onClick={() => setIsNewSession(false)}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
              </CardActions>
            </Card>
          </Container>
        ) : currentSession ? (
          // Active session view
          <Box>
            {/* Progress Header */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                noWrap
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                }}
              >
                {currentSession.originalAsk.substring(0, isMobile ? 40 : 60)}...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(getStepperActiveStep() / stepperSteps.length) * 100}
                sx={{ mb: { xs: 1.5, sm: 2 } }}
              />
              <Stepper 
                activeStep={getStepperActiveStep()} 
                alternativeLabel={!isMobile}
                orientation={isMobile ? 'vertical' : 'horizontal'}
              >
                {stepperSteps.map((step) => (
                  <Step key={step}>
                    <StepLabel icon={getStepIcon(step)}>
                      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                        {getStepDisplayName(step)}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>

            {/* Chat Messages */}
            <Paper 
              sx={{ 
                p: { xs: 1, sm: 1.5, md: 2 }, 
                mb: { xs: 1.5, sm: 2 }, 
                minHeight: { xs: 300, sm: 400 }, 
                maxHeight: { xs: '50vh', sm: 600 }, 
                overflow: 'auto',
                '& pre': {
                  overflowX: 'auto',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            >
              {currentSession.messages.map((message) => (
                <MessageComponent
                  key={message.id}
                  message={message}
                  session={currentSession}
                  onEditNarrative={() => {
                    // Save the session when narrative is edited
                    IncubationService.saveToLocalStorage(currentSession);
                    setCurrentSession({ ...currentSession });
                  }}
                />
              ))}
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', p: { xs: 1.5, sm: 2 } }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {processingMessage}
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Paper>

            {/* Action Area */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              {currentSession.state !== WorkflowState.Completed ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 2}
                    variant="outlined"
                    placeholder={isMobile ? "Additional instructions..." : "Enter additional instructions for the next step (optional)..."}
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    sx={{ mb: { xs: 1.5, sm: 2 } }}
                  />
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={!isMobile && <PlayIcon />}
                      onClick={() => executeNextStep()}
                      disabled={loading}
                      fullWidth={isMobile}
                      size={isMobile ? 'medium' : 'large'}
                    >
                      {getNextStep(currentSession.lastExecutedStep) === WorkflowStepType.None
                        ? 'Complete'
                        : isMobile 
                          ? getStepDisplayName(getNextStep(currentSession.lastExecutedStep))
                          : `Next: ${getStepDisplayName(getNextStep(currentSession.lastExecutedStep))}`}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={!isMobile && <DownloadIcon />}
                      onClick={() => exportSession('json')}
                      fullWidth={isMobile}
                      size={isMobile ? 'medium' : 'large'}
                    >
                      Export
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Incubation Complete!"
                  color="success"
                  size="medium"
                />
              )}
            </Paper>
          </Box>
        ) : (
          // Welcome screen
          <Container 
            maxWidth="sm"
            sx={{ px: { xs: 1, sm: 2 } }}
          >
            <Card>
              <CardContent 
                sx={{ 
                  textAlign: 'center', 
                  py: { xs: 3, sm: 4 },
                  px: { xs: 2, sm: 3 },
                }}
              >
                <RocketIcon 
                  sx={{ 
                    fontSize: { xs: 60, sm: 80 }, 
                    color: 'primary.main', 
                    mb: { xs: 1.5, sm: 2 },
                  }} 
                />
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                  }}
                >
                  Welcome to Incubation Studio
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  Transform your ideas into fully-architected systems through intelligent design workflows.
                </Typography>
                <Button
                  variant="contained"
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<AddIcon />}
                  onClick={() => setIsNewSession(true)}
                  fullWidth={isMobile}
                >
                  Start New Session
                </Button>
              </CardContent>
            </Card>
          </Container>
        )}
      </Box>
    </Box>
  );
};